import { useEffect, useMemo, useState } from "react";
import API from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Crosshair, CopySimple, DownloadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

export const GapRecruitmentList = () => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (open && !data) {
      API.get("/suppliers/recruitment-gaps").then(({ data }) => setData(data)).catch(() => toast.error("Failed to load gaps"));
    }
  }, [open, data]);

  const rows = useMemo(() => {
    const gaps = data?.gaps || [];
    const q = search.trim().toLowerCase();
    if (!q) return gaps;
    return gaps
      .map((g) => {
        if (g.service.toLowerCase().includes(q)) return g;
        const matched = g.missing_counties.filter((c) => c.toLowerCase().includes(q));
        return matched.length ? { ...g, missing_counties: matched } : null;
      })
      .filter(Boolean);
  }, [data, search]);

  const comboCount = rows.reduce((n, g) => n + g.missing_counties.length, 0);

  const buildText = () => {
    const lines = [
      `Supplier Recruitment Hit-List — ${new Date().toLocaleDateString("en-GB")}`,
      `${comboCount} county-service gaps across ${rows.length} services`,
      `Apply to join our supplier network: ${window.location.origin}/apply`,
      "",
    ];
    rows.forEach((g) => {
      lines.push(`${g.service.toUpperCase()} — needed in ${g.missing_counties.length} counties:`);
      lines.push(g.missing_counties.join(", "));
      lines.push("");
    });
    return lines.join("\n");
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(buildText());
      toast.success("Hit-list copied to clipboard");
    } catch {
      toast.error("Copy failed — try the CSV download");
    }
  };

  const handleCsv = () => {
    const esc = (s) => `"${String(s).replace(/"/g, '""')}"`;
    const csv = ["Service,County", ...rows.flatMap((g) => g.missing_counties.map((c) => `${esc(g.service)},${esc(c)}`))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `supplier-recruitment-gaps-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success("CSV downloaded");
  };

  return (
    <>
      <Button variant="outline" size="sm" className="w-full text-xs" onClick={() => setOpen(true)} data-testid="recruitment-hitlist-btn">
        <Crosshair size={13} className="mr-1" /> Recruitment hit-list
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl" data-testid="recruitment-hitlist-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo]">Supplier Recruitment Hit-List</DialogTitle>
            <DialogDescription>
              {data ? `${comboCount} county-service gaps across ${rows.length} services — share this list to target new supplier sign-ups.` : "Loading gaps..."}
            </DialogDescription>
          </DialogHeader>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filter by service or county... e.g. Drainage or Kent"
            className="h-8 text-xs"
            data-testid="hitlist-search"
          />
          <div className="max-h-[50vh] overflow-y-auto space-y-3 pr-1" data-testid="hitlist-rows">
            {rows.map((g) => (
              <div key={g.service} className="border border-border rounded-md p-3" data-testid={`hitlist-row-${g.service.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-sm font-semibold">{g.service}</p>
                  <Badge variant="outline" className="text-[9px] text-red-600 dark:text-red-400 border-red-200 dark:border-red-900">
                    needed in {g.missing_counties.length} {g.missing_counties.length === 1 ? "county" : "counties"}
                  </Badge>
                </div>
                <p className="text-[11px] text-muted-foreground leading-relaxed">{g.missing_counties.join(", ")}</p>
              </div>
            ))}
            {data && rows.length === 0 && <p className="text-xs text-muted-foreground text-center py-6">No gaps match your filter.</p>}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" size="sm" onClick={handleCopy} data-testid="hitlist-copy-btn">
              <CopySimple size={13} className="mr-1" /> Copy to clipboard
            </Button>
            <Button size="sm" onClick={handleCsv} data-testid="hitlist-csv-btn">
              <DownloadSimple size={13} className="mr-1" /> Download CSV
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
